import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiCheck, FiHeart, FiMinus, FiPlus, FiX } from "react-icons/fi";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/productdetails.scss";

const getSwatchColour = (colour) => {
  const value = colour.toLowerCase();
  if (value.includes("brown")) return "#76513b";
  if (value.includes("olive")) return "#66684c";
  if (value.includes("red")) return "#8f2929";
  if (value.includes("white")) return "#f2efe7";
  if (value.includes("silver")) return "#aaa9a5";
  if (value.includes("black")) return "#171717";
  return "#182b50";
};

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setAdded(false);
    setImageLoaded(false);
    setImageError(false);
    setSizeGuideOpen(false);
  }, [product]);

  useEffect(() => {
    if (!sizeGuideOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSizeGuideOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [sizeGuideOpen]);

  const relatedProducts = useMemo(
    () =>
      productData
        .filter((item) => item.category === product?.category && item.id !== product?.id)
        .slice(0, 4),
    [product]
  );

  if (!product) {
    return (
      <main className="product-not-found page-shell">
        <span className="eyebrow">404 · Product unavailable</span>
        <h1>Product not found</h1>
        <p>The piece you are looking for may have moved or sold through.</p>
        <Link className="button-primary" to="/shop">Return to shop</Link>
      </main>
    );
  }

  const maxQuantity = Math.max(1, Math.min(10, Number(product.stock) || 1));

  const addProduct = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
  };

  return (
    <main className="product-detail">
      <section className="product-detail__top">
        <div className="product-gallery" aria-label={`${product.name} gallery`}>
          <div className={`product-gallery__main ${imageLoaded ? "is-loaded" : ""}`}>
            {!imageError ? (
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="product-gallery__fallback">Image unavailable</div>
            )}
            {product.newArrival && <span className="product-gallery__badge">New</span>}
            <span className="product-gallery__counter">
              {selectedImage + 1} / {product.images.length}
            </span>
          </div>
          {product.images.length > 1 && (
            <div className="product-gallery__thumbs">
              {product.images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={index === selectedImage ? "is-active" : ""}
                  onClick={() => {
                    setSelectedImage(index);
                    setImageLoaded(false);
                    setImageError(false);
                  }}
                  aria-label={`View image ${index + 1}`}
                >
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="product-info">
          <div className="product-info__heading">
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="eyebrow">
              {product.category}
            </Link>
            <h1>{product.name}</h1>
            <div className="product-info__price">
              <strong>{formatPrice(product.price)}</strong>
              {product.compareAtPrice && <del>{formatPrice(product.compareAtPrice)}</del>}
            </div>
            <span className="product-info__sku">{product.sku}</span>
          </div>

          <div className="product-info__option">
            <div className="product-info__option-head">
              <span>Colour</span><strong>{selectedColor}</strong>
            </div>
            <div className="colour-options">
              {product.colors.map((color) => (
                <button
                  type="button"
                  key={color}
                  className={color === selectedColor ? "is-active" : ""}
                  onClick={() => setSelectedColor(color)}
                  aria-pressed={color === selectedColor}
                >
                  <span
                    aria-hidden="true"
                    style={{ "--swatch-colour": getSwatchColour(color) }}
                  /> {color}
                </button>
              ))}
            </div>
          </div>

          <div className="product-info__option">
            <div className="product-info__option-head">
              <span>Size</span>
              <button
                type="button"
                aria-expanded={sizeGuideOpen}
                aria-controls="product-size-guide"
                onClick={() => setSizeGuideOpen(true)}
              >
                Size guide
              </button>
            </div>
            <div className="size-options">
              {product.size.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={size === selectedSize ? "is-active" : ""}
                  onClick={() => setSelectedSize(size)}
                  aria-pressed={size === selectedSize}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div className="product-info__quantity">
            <span>Quantity</span>
            <div>
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity" disabled={quantity <= 1}><FiMinus /></button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((value) => Math.min(maxQuantity, value + 1))} aria-label="Increase quantity" disabled={quantity >= maxQuantity}><FiPlus /></button>
            </div>
          </div>

          <div className="product-info__actions">
            <button type="button" className="button-primary" onClick={addProduct} disabled={!selectedSize || product.stock < 1}>
              {product.stock < 1 ? "Out of stock" : `Add to bag · ${formatPrice(product.price * quantity)}`}
            </button>
            <button
              type="button"
              className={`wishlist-button ${isWishlisted(product.id) ? "is-active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-label={isWishlisted(product.id) ? "Remove from wishlist" : "Add to wishlist"}
            >
              <FiHeart />
            </button>
          </div>

          {added && (
            <div className="product-info__added" role="status">
              <FiCheck /> Added to your bag. <Link to="/cartpage">Review bag</Link>
            </div>
          )}

          <div className="product-info__assurances">
            <span>28 day returns</span>
            <span>Made to be worn</span>
            <span>Free shipping over {formatPrice(siteConfig.shippingThreshold)}</span>
          </div>

          <div className="product-info__description">
            {product.description.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>

          <div className="product-info__accordions">
            <details open>
              <summary>Product details</summary>
              <ul>{product.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
            </details>
            <details>
              <summary>Fabric &amp; fit</summary>
              <dl>
                {Object.entries(product.attributes).map(([label, value]) => (
                  <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                ))}
              </dl>
            </details>
            <details>
              <summary>Delivery &amp; returns</summary>
              <p>Tracked delivery from the UK. Returns are accepted within 28 days when the item is unworn and tagged.</p>
            </details>
          </div>
        </aside>
      </section>

      {sizeGuideOpen && (
        <div
          className="size-guide-layer"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSizeGuideOpen(false);
          }}
        >
          <section
            className="size-guide"
            id="product-size-guide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="size-guide-title"
          >
            <header>
              <div>
                <span className="eyebrow">Fit assistance</span>
                <h2 id="size-guide-title">Size guide</h2>
              </div>
              <button type="button" onClick={() => setSizeGuideOpen(false)} aria-label="Close size guide">
                <FiX />
              </button>
            </header>
            <p>
              This piece is listed in tagged sizes. Compare a similar garment you
              already own and allow for the substantial hand of the fabric.
            </p>
            <dl>
              <div><dt>Fit</dt><dd>{product.attributes.Fit}</dd></div>
              <div><dt>Fabric</dt><dd>{product.attributes.Fabric}</dd></div>
              <div><dt>Available sizes</dt><dd>{product.size.join(" · ")}</dd></div>
            </dl>
            <div className="size-guide__steps">
              <p><strong>01</strong><span>Lay your best-fitting garment flat without stretching it.</span></p>
              <p><strong>02</strong><span>Measure across the key points and double widths where needed.</span></p>
              <p><strong>03</strong><span>For personal fit advice, send those measurements to our team.</span></p>
            </div>
            <Link className="button-primary" to="/contact">Ask for sizing help</Link>
          </section>
        </div>
      )}

      <section className="product-detail__story page-shell">
        <div>
          <span className="eyebrow">Fabric focus</span>
          <h2>Built for the long haul</h2>
        </div>
        <p>{product.material}. Each piece is cut for hard wear, slow break-in and the kind of character that only comes with time.</p>
        <p>Wash sparingly at 30°C, avoid tumble drying and let the fabric tell the story of how you wear it.</p>
      </section>

      {relatedProducts.length > 0 && (
        <section className="product-related page-shell">
          <div className="product-related__heading">
            <h2>You may also like</h2>
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>View category →</Link>
          </div>
          <div className="product-related__grid">
            {relatedProducts.map((item) => (
              <Link key={item.id} to={`/shopDetails/${item.id}`}>
                <div className="product-related__image"><img src={item.image} alt={item.name} loading="lazy" /></div>
                <strong>{item.name}</strong>
                <span>{item.sku} · {formatPrice(item.price)}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
